/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, reaction, spawn } from '@reatom/framework'

import { BigNumber, Iterable, pipe } from '@blade/std-lib'
import { quickSwapAmountBySectionAtom } from '@root/features/trade/trade-settings/atoms'
import { withEquivalence } from '@root/shared/lib/reatom/with-equivalence'

import { quoteSubscriptionRegistry } from './reatom-quote-registry'

import type { QuoteRegistryKeyParams } from './reatom-quote-registry'
import type { ReactionAtom } from '@reatom/framework'
import type { PairAddress, PairModel } from '@root/entities/pair'
import type { TradeSettingsQuick } from '@root/entities/trade-settings/model'

export const reatomQuickBuyQuoteBulkSubscription = ({
  subscriberKey,
  section,
  autoSubscribe = true,
}: {
  subscriberKey: string
  section: TradeSettingsQuick.QuickSwapSection
  autoSubscribe?: boolean
}) => {
  const quickBuyAtomAtom = atom((ctx) => {
    const bySection = ctx.spy(quickSwapAmountBySectionAtom)
    if (!bySection) {
      return BigNumber.makeUnsafe(0)
    }

    return bySection[section].buy
  }).pipe(withEquivalence(BigNumber.equivalence))

  const trackedKeysByPair = new Map<PairAddress, QuoteRegistryKeyParams>()

  const releasePairAction = action((ctx, pairAddress: PairAddress) => {
    const trackedParams = trackedKeysByPair.get(pairAddress)
    if (!trackedParams) {
      return
    }

    quoteSubscriptionRegistry.remove(
      ctx,
      trackedParams,
      subscriberKey,
      (model) => {
        model.unsubscribe(ctx)
      },
    )
    trackedKeysByPair.delete(pairAddress)
  })

  let reactionAtom: ReactionAtom<unknown> | undefined

  const processPairsReaction = reaction((ctx, pairs: Array<PairModel>) => {
    const amount = ctx.spy(quickBuyAtomAtom)

    if (amount.isZero()) {
      unsubscribeAllAction(ctx)
      return
    }

    const pairsById = pipe(
      pairs,
      Iterable.toMap((pair) => pair.address),
    )

    for (const pair of pairs) {
      const existingQuoteParams = trackedKeysByPair.get(pair.address)

      if (existingQuoteParams) {
        if (existingQuoteParams.amount.eq(amount)) {
          continue
        }

        releasePairAction(ctx, pair.address)
      }

      const params: QuoteRegistryKeyParams = {
        pair: pair,
        amount,
        type: 'buy',
      }

      quoteSubscriptionRegistry.set(ctx, params, subscriberKey, (model) => {
        if (autoSubscribe) {
          model.subscribe(ctx)
        }
      })

      trackedKeysByPair.set(pair.address, params)
    }

    for (const pairAddress of trackedKeysByPair.keys()) {
      if (!pairsById.has(pairAddress)) {
        releasePairAction(ctx, pairAddress)
      }
    }
  })

  const processPairsAction = action((ctx, pairs: Array<PairModel>) => {
    reactionAtom?.unsubscribe()
    reactionAtom = spawn(ctx, () => processPairsReaction(ctx, pairs))
  })

  const unsubscribeAllAction = action((ctx) => {
    for (const pairAddress of trackedKeysByPair.keys()) {
      releasePairAction(ctx, pairAddress)
    }
    reactionAtom?.unsubscribe()
    reactionAtom = undefined
  })

  return {
    processPairs: processPairsAction,
    unsubscribeAll: unsubscribeAllAction,
  }
}
